/**
 * SaveAccountCard: inline guest → real-account conversion (Google or
 * email+password). Links the credential to the SAME anonymous uid, so every
 * character / session / token balance carries over (see `convertToAccount` /
 * `convertWithGoogle` in auth-service.ts). On success the auth store flips
 * `isAnonymous`, so any parent gated on `user.isAnonymous` re-renders itself.
 *
 * Reusable so guest upsells (Invite Friends, Rewards, the dashboard hero card)
 * offer the save in-place instead of pointing the player elsewhere.
 */

import { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { convertToAccount, convertWithGoogle } from '@/lib/auth-service';
import { toast } from '@/lib/toast';

interface SaveAccountCardProps {
  title?: string;
  description?: string;
  /** Fired after a successful convert (either path), for optional parent side effects. */
  onSaved?: () => void;
}

export function SaveAccountCard({
  title = 'Save your account',
  description = "You're playing as a guest. Add an email and password to keep your hero, play on any device, and unlock more of Polymyth.",
  onSaved,
}: SaveAccountCardProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleConvert = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (password.length < 6) {
        setError('Password must be at least 6 characters.');
        return;
      }
      setLoading(true);
      try {
        await convertToAccount(email, password);
        toast.success('Account saved! You can now play on any device.');
        setEmail('');
        setPassword('');
        onSaved?.();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Could not save your account. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    },
    [email, password, onSaved]
  );

  const handleConvertGoogle = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await convertWithGoogle();
      toast.success('Account saved with Google!');
      onSaved?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not sign in with Google. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [onSaved]);

  return (
    <div className="rounded-md border border-[hsl(var(--accent-gold))]/40 bg-card p-4 space-y-3">
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <Button
        type="button"
        onClick={handleConvertGoogle}
        disabled={loading}
        variant="outline"
        className="w-full"
      >
        Continue with Google
      </Button>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">or</span>
        </div>
      </div>

      <form onSubmit={handleConvert} className="space-y-3">
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/15 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
        <Input
          type="email"
          name="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Input
          type="password"
          name="new-password"
          autoComplete="new-password"
          placeholder="Password (6+ characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={6}
        />
        <Button
          type="submit"
          className="w-full bg-[hsl(var(--accent-gold))] text-white hover:bg-[hsl(var(--accent-gold))]/90"
          disabled={loading}
        >
          {loading ? 'Saving…' : 'Save account'}
        </Button>
      </form>
    </div>
  );
}
